import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router';
import { GENRES } from '../../lib/genre.ts';
import './Header.css';

export default function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const set = () => {
      document.documentElement.style.setProperty('--header-h', `${String(el.offsetHeight)}px`);
    };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, []);

  const isHome = pathname === '/';
  const overHero = isHome && !scrolled;

  useEffect(() => {
    setQ(params.get('q') ?? '');
  }, [params]);

  useEffect(() => {
    const onScroll = () => { setScrolled(window.scrollY > 40); };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); };
  }, []);

  function runSearch() {
    const query = q.trim();
    void navigate(query ? `/browse?q=${encodeURIComponent(query)}` : '/browse');
  }

  return (
    <header ref={headerRef} className={`hdr${overHero ? ' hdr--over' : ''}${scrolled ? ' hdr--scrolled' : ''}`}>
      <div className="hdr__bar">
        <Link to="/" className="hdr__logo" aria-label="NatyaX home">
          <svg viewBox="0 0 32 32" className="hdr__mark" aria-hidden="true">
            <defs>
              <linearGradient id="hdr-logo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%"   stopColor="#ff6b00" />
                <stop offset="100%" stopColor="#e91e8c" />
              </linearGradient>
            </defs>
            <rect width="32" height="32" rx="8" fill="url(#hdr-logo-grad)" />
            <path d="M8 8h4v16H8zM20 8h4v16h-4zM12 8h4L20 24h-4z" fill="#ffffff" />
          </svg>
          <span className="hdr__word">
            Natya<span className="hdr__word-accent">X</span>
          </span>
        </Link>

        <form
          className="hdr__search"
          role="search"
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="hdr__search-icon">
            <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
          </svg>
          <input
            type="search"
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
            placeholder="Search movies, genres…"
            aria-label="Search videos"
          />
        </form>

        <nav className="hdr__top-nav" aria-label="Main navigation">
          <NavLink to="/browse" end className="hdr__top-link">Collection</NavLink>
          <NavLink to="/subscribe" className="hdr__top-link">Plans</NavLink>
          <NavLink to="/account" className="hdr__top-link">Account</NavLink>
        </nav>
      </div>

      <nav className="hdr__nav" aria-label="Genres">
        {GENRES.map((g) => (
          <NavLink
            key={g.slug}
            to={`/browse?genre=${g.slug}`}
            className="hdr__link"
          >
            {g.name}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
